import { useState } from "react";
import { notifyApiErrors } from "../../../core/helpers/helpers";
import useTranslations from "../../../core/i18n/useTranslations";
import useUser from "../../../core/user/useUser";
import Button from "../../../ui-lib/components/Button/Button";
import { Spacer } from "../../../components/Layout/Layout";
import { NewRoleTemplate } from "../../../core/api/roleTemplates/types";
import { useObjectForm } from "../../../core/SaveObjectContext/hooks";
import { createRoleTemplate } from "../../../core/api/roleTemplates/roleTemplates";
import { RoleTemplateGeneralForm } from "./RoleTemplateGeneralForm";
import { notify } from "../../../ui-lib/components/Alerts/Toast";
import {
  DateTimeFormat,
  Timezone,
} from "../../../core/api/administrators/types";
import PrimeModal from "../../../ui-lib/components/PrimeModal/PrimeModal";

export const AddRoleTemplate = ({
  onSubmit,
  onClose,
  timezones,
  dateTimeFormats,
}: {
  onSubmit: () => void;
  onClose?: () => void;
  timezones: Timezone[];
  dateTimeFormats: DateTimeFormat[];
}) => {
  const t = useTranslations();
  const { authenticatedRequest, config } = useUser();
  const [isLoading, setIsLoading] = useState(false);

  const objectForm = useObjectForm<NewRoleTemplate>({
    defaultValues: {
      type: "admin",
      respondentGroups: [],
      hierarchyUsage: 3,
      primaryLanguage: config?.primaryLanguage ?? "en-GB",
      secondaryLanguage: config?.secondaryLanguage ?? "en-GB",
      dateTimeFormatId: dateTimeFormats
        ? dateTimeFormats.find(
            (timeFormat) =>
              timeFormat.dateTimeFormat.toLowerCase() ===
              config?.timezone.datetimeFormat.toLowerCase()
          )?.id
        : 1,
      timezoneId: timezones
        ? timezones.find(
            (timezone) =>
              timezone.timezoneName === config?.timezone.timezoneName
          )?.id
        : 7,
    },
  });

  const [isAddTemplateOpen, setIsAddTemplateOpen] = useState(false);

  const closeModal = () => {
    objectForm.reset();

    setIsAddTemplateOpen(false);
    if (onClose !== undefined) {
      onClose();
    }
  };

  const onCreateTemplateSubmit = async (formData: NewRoleTemplate) => {
    setIsLoading(true);
    try {
      if (formData.responseCenterId) formData.organizationId = undefined;
      await createRoleTemplate(formData, authenticatedRequest);
      onSubmit();
      notify({
        message: t("Administrator:role_template_created_success"),
      });
      closeModal();
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="primary"
        text={t("Administrator:new_role_template")}
        onClick={() => {
          setIsAddTemplateOpen(true);
        }}
      />
      <PrimeModal
        withHeader
        withFooter
        header={t("Administrator:create_new_role_template")}
        isOpen={isAddTemplateOpen}
        onClose={closeModal}
        submitBtn={{
          text: t("Administrator:create_role_template"),
          onClick: objectForm.handleSubmit(onCreateTemplateSubmit),
          disabled: isLoading,
        }}
        cancelBtn={{
          text: t("Common:cancel"),
          onClick: closeModal,
        }}
        loading={isLoading}
        fixedWidth="1000px"
      >
        <p>{t("Administrator:add_role_template_description")}</p>
        <Spacer size={16} />
        <RoleTemplateGeneralForm
          authenticatedRequest={authenticatedRequest}
          objectForm={objectForm}
          isEditPage={false}
          timezones={timezones}
          dateTimeFormats={dateTimeFormats}
        />
      </PrimeModal>
    </>
  );
};
